// The round-start roll call (core/roll-call.js owns the timeline and sizes the
// start phase with it): during the first rollCallTime seconds of the start
// phase every player is introduced in turn, top to bottom in the scoreboard's
// order. On the board the head (and its launch arrow) fades in with a tag
// beside it holding the flag and the name; the tags stay up, linger once
// everyone is on, and fade out together into the countdown, gone with its "3"
// (TAGS_INTO_COUNTDOWN). On the rail the player's row lights up on the same beat. Every
// beat here comes from core's rollCallSchedule, so board, rail and server
// always agree, whatever the roster size or the tuning.
import * as scoreUtil from "core/src/core/score/score-util.js";
import { rollCallSchedule, rollCallBeats } from "core/src/core/roll-call.js";
import type { ScoreMap } from "./types";

export const INTRO_FADE = 0.3;      // a head's fade-in
export const TAG_IN = 0.2;          // the tag's fade-in; it then stays until the schedule's fade
// The tags stay into the countdown this long (its "3"), so they are gone as the
// "3" is, not before the countdown starts. Never past the start phase's end.
export const TAGS_INTO_COUNTDOWN = 1.0;

// The roll call's timing for this game, in seconds into the start phase.
export interface IntroSchedule {
  delays: Record<string, number>; // each player's beat
  fadeAt: number;                 // when the tags start fading out
  fadeDur: number;                // how long that fade takes
  end: number;                    // when the countdown proper starts (the game's rollCallTime)
}

type RollCallGame = { rollCallTime?: number; startPhaseTime?: number } | null | undefined;

// Everything a screen needs to time the roll call. Empty (no beats, zero
// times) when the game has no roll call (rollCallTime 0: the shop's try-out,
// old replays). The end is the GAME's rollCallTime, the authoritative length
// the server sized the start phase with; the fade is anchored to it and never
// starts before the last beat, so even a server on other tuning can't make the
// tags vanish before everyone has been introduced.
export function introSchedule(players: { id: string }[], startScore: ScoreMap | null | undefined, gameState: RollCallGame): IntroSchedule {
  const end = (gameState && gameState.rollCallTime) || 0;
  if (!end) return { delays: {}, fadeAt: 0, fadeDur: 0, end: 0 };
  const order = introOrder(players, startScore);
  // Tied leaders are introduced together on the first beat (core's
  // rollCallBeats, the same rule the server sized the start phase with).
  const leaders = introLeaders(players, startScore);
  const s = rollCallSchedule(rollCallBeats(order, startScore));
  const delays: Record<string, number> = {};
  let beat = 0;
  order.forEach((id) => {
    if (leaders.length > 1 && leaders.indexOf(id) !== -1) {
      delays[id] = s.delays[0];
      return;
    }
    if (leaders.length > 1 && beat === 0) beat = 1; // the leaders took beat 0
    delays[id] = s.delays[Math.min(beat, s.delays.length - 1)];
    beat++;
  });
  const fadeDur = Math.min(s.fade, end);
  const startPhase = (gameState && gameState.startPhaseTime) || end + TAGS_INTO_COUNTDOWN;
  const into = Math.max(0, Math.min(TAGS_INTO_COUNTDOWN, startPhase - end));
  const fadeAt = Math.max(s.lastBeat, end - fadeDur) + into;
  return { delays, fadeAt, fadeDur, end };
}

// Player ids in the order they are introduced: the scoreboard's order at the
// round start (by score, ties in roster order), which is exactly how Score.tsx
// ranks its rows before the round-end settle.
export function introOrder(players: { id: string }[], startScore?: ScoreMap | null): string[] {
  const map: ScoreMap = {};
  players.forEach((p) => {
    map[p.id] = (startScore && startScore[p.id]) || 0;
  });
  return scoreUtil.createSortedList(map).map((ps: any) => ps.id as string);
}

// Seconds into the start phase at which each player is introduced (see
// introSchedule for the rest of the timing).
export function introDelays(players: { id: string }[], startScore: ScoreMap | null | undefined, gameState: RollCallGame): Record<string, number> {
  return introSchedule(players, startScore, gameState).delays;
}

// The players introduced as the leader(s): everyone on the round's top start
// score, as long as someone is below it (a top everyone shares, as in round
// 1, has no leader: empty). They get the gold banner on the board, the gold
// row on the rail, and share the first beat.
export function introLeaders(players: { id: string }[], startScore?: ScoreMap | null): string[] {
  const map: ScoreMap = {};
  players.forEach((p) => {
    map[p.id] = (startScore && startScore[p.id]) || 0;
  });
  const sorted = scoreUtil.createSortedList(map);
  if (sorted.length < 2) return [];
  const top = sorted[0].score;
  if (sorted[sorted.length - 1].score >= top) return [];
  return sorted.filter((ps: any) => ps.score === top).map((ps: any) => ps.id as string);
}

// 0..1 how far a head's fade-in is at render time t (1 outside the start phase).
export function introAlpha(t: number, delay: number | undefined, startPhase: number): number {
  if (delay === undefined || t >= startPhase) return 1;
  return Math.max(0, Math.min(1, (t - delay) / INTRO_FADE));
}
